import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Nav, Badge, ListGroup, Spinner, Alert } from 'react-bootstrap';
import { fetchBoardYears, fetchPastPresidents } from '../services/api';

const BoardOfDirectors = () => {
    const [selectedYear, setSelectedYear] = useState(null);
    const [boardData, setBoardData] = useState({});
    const [pastPresidents, setPastPresidents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                // Fetch board years via API service
                const boardYears = await fetchBoardYears();
                
                // Transform array to object keyed by period to preserve previous component logic
                const boardMap = {};
                boardYears.forEach(y => { boardMap[y.title || y.period] = y; });
                setBoardData(boardMap);
                setSelectedYear(boardYears[0].title || boardYears[0].period);
                
                // Fetch past presidents via API service
                const presidents = await fetchPastPresidents();
                setPastPresidents(presidents);

                setError(null);
            } catch (err) {
                console.error('Error fetching board data:', err);
                setError('Failed to load board data. Please try again later.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Generate year list from 2024 down to 2003
    const availableYears = [];
    
    for (let year = (parseInt((new Date()).getFullYear())); year >= 2003; year--) {
        const yearLabel = `${year}-${year + 1}`;
        availableYears.push(yearLabel);
    }

    // Get current board data
    const currentBoard = boardData[selectedYear] || {};

    // Loading state
    if (loading) {
        return (
            <div className="board-page py-5" style={{ paddingTop: '160px !important' }}>
                <Container>
                    <div className="text-center">
                        <Spinner animation="border" role="status" variant="primary">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                        <p className="mt-2">Loading board information...</p>
                    </div>
                </Container>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="board-page py-5" style={{ paddingTop: '160px !important' }}>
                <Container>
                    <Alert variant="danger">
                        <Alert.Heading>Error Loading Board Data</Alert.Heading>
                        <p>{error}</p>
                    </Alert>
                </Container>
            </div>
        );
    }

    return (
        <div className="board-page py-5" style={{ paddingTop: '160px !important' }}>
            <Container>
                <Row>
                    <Col lg={12}>
                        {/* Page Header */}
                        <div className="text-center mb-5">
                            <h1 className="display-4 fw-bold text-primary mb-3">
                                Board of Directors
                            </h1>
                            <p className="lead text-muted">
                                Leadership and Governance of the Multinational Finance Society
                            </p>
                            <hr className="mx-auto" style={{ width: '100px', height: '3px' }} />
                        </div>

                        <Row className="g-4">
                            {/* Left Sidebar - Year Navigation */}
                            <Col lg={3} className="mb-4">
                                <Card className="shadow-sm">
                                    <Card.Header className="bg-primary bg-gradient text-white">
                                        <h5 className="mb-0">
                                            <i className="bi bi-calendar3 me-2"></i>
                                            Board Years
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        <ListGroup variant="flush">
                                            {availableYears.map((year) => (
                                                <ListGroup.Item
                                                    key={year}
                                                    action
                                                    active={selectedYear === year}
                                                    onClick={() => setSelectedYear(year)}
                                                    className="d-flex justify-content-between align-items-center"
                                                >
                                                    <span>{year}</span>
                                                    {boardData[year] && (
                                                        <Badge bg="success" pill>
                                                            <i className="bi bi-check"></i>
                                                        </Badge>
                                                    )}
                                                    {!boardData[year] && (
                                                        <Badge bg="secondary" pill>
                                                            <i className="bi bi-hourglass-split"></i>
                                                        </Badge>
                                                    )}
                                                </ListGroup.Item>
                                            ))}
                                        </ListGroup>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Middle Column - Main Content Area */}
                            <Col lg={6}>
                                {/* Board Content */}
                                {boardData[selectedYear] ? (
                                    <div>
                                        {/* Year Header */}
                                        <div className="mb-4">
                                            <h4 className="text-primary mb-2">
                                                <i className="bi bi-people me-2"></i>
                                                Board ({currentBoard.period || selectedYear})
                                            </h4>
                                            <Badge bg={boardData[selectedYear] ? "success" : "warning"}>
                                                {boardData[selectedYear] ? "Available" : "Coming Soon"}
                                            </Badge>
                                        </div>
                                        {/* Sponsoring Institution Section */}
                                        {currentBoard.sponsoringInstitution && (
                                            <div className="mb-4">
                                                <h5 className="text-info mb-3">
                                                    <i className="bi bi-bank me-2"></i>
                                                    SPONSORING INSTITUTION
                                                </h5>
                                                <div className="bg-light p-3 rounded">
                                                    <p className="mb-0 fw-semibold">{currentBoard.sponsoringInstitution}</p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Board of Directors Section */}
                                        {currentBoard.boardOfDirectors && (
                                            <div className="mb-4">
                                                <h5 className="text-primary mb-3">
                                                    <i className="bi bi-people-fill me-2"></i>
                                                    BOARD OF DIRECTORS
                                                </h5>
                                                <div className="row g-2">
                                                    {currentBoard.boardOfDirectors.map((member, index) => (
                                                        <div key={index} className="col-12 mb-2">
                                                            <div className="card shadow-sm">
                                                                <div className="card-body p-3">
                                                                    <div className="d-flex justify-content-between align-items-start mb-1">
                                                                        <h6 className="text-primary mb-0 small">{index + 1}. {member.position}</h6>
                                                                        {member.term && (
                                                                            <Badge bg="secondary" className="small">{member.term}</Badge>
                                                                        )}
                                                                    </div>
                                                                    <div className="fw-semibold">
                                                                        {member.name === "TBA" ? (
                                                                            <span className="text-muted fst-italic">To Be Announced</span>
                                                                        ) : (
                                                                            member.name
                                                                        )}
                                                                    </div>
                                                                    {member.affiliation && (
                                                                        <p className="text-muted small mb-0">
                                                                            <i className="bi bi-building me-1"></i>
                                                                            {member.affiliation}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Board of Trustees Section */}
                                        {currentBoard.boardOfTrustees && (
                                            <div className="mb-4">
                                                <h5 className="text-info mb-3">
                                                    <i className="bi bi-shield-check me-2"></i>
                                                    BOARD OF TRUSTEES *
                                                </h5>
                                                <p className="text-muted small mb-3">
                                                    <em>* Founding Members & Past Presidents (active MFS members, only)</em>
                                                </p>
                                                <div className="row g-2">
                                                    {currentBoard.boardOfTrustees.map((member, index) => (
                                                        <div key={index} className="col-12 mb-2">
                                                            <div className="card shadow-sm">
                                                                <div className="card-body p-3">
                                                                    <div className="fw-semibold">{index + 1}. {member.name}</div>
                                                                    {member.affiliation && (
                                                                        <p className="text-muted small mb-0">
                                                                            <i className="bi bi-building me-1"></i>
                                                                            {member.affiliation}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Executive Committee Section */}
                                        {currentBoard.executiveCommittee && (
                                            <div className="mb-4">
                                                <h5 className="text-success mb-3">
                                                    <i className="bi bi-gear me-2"></i>
                                                    EXECUTIVE COMMITTEE
                                                </h5>
                                                <div className="row g-2">
                                                    {currentBoard.executiveCommittee.map((member, index) => (
                                                        <div key={index} className="col-12 mb-2">
                                                            <div className="card shadow-sm">
                                                                <div className="card-body p-3">
                                                                    <div className="d-flex justify-content-between align-items-start mb-1">
                                                                        <h6 className="text-primary mb-0 small">{index + 1}. {member.position}</h6>
                                                                        {member.term && (
                                                                            <Badge bg="secondary" className="small">{member.term}</Badge>
                                                                        )}
                                                                    </div>
                                                                    <div className="fw-semibold">
                                                                        {member.name === "TBA" ? (
                                                                            <span className="text-muted fst-italic">To Be Announced</span>
                                                                        ) : (
                                                                            member.name
                                                                        )}
                                                                    </div>
                                                                    {member.affiliation && (
                                                                        <p className="text-muted small mb-0">
                                                                            <i className="bi bi-building me-1"></i>
                                                                            {member.affiliation}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Nominating Committee Section */}
                                        {currentBoard.nominatingCommittee && (
                                            <div className="mb-4">
                                                <h5 className="text-secondary mb-3">
                                                    <i className="bi bi-person-check me-2"></i>
                                                    NOMINATING COMMITTEE
                                                </h5>
                                                <div className="row g-2">
                                                    {currentBoard.nominatingCommittee.map((member, index) => (
                                                        <div key={index} className="col-12 mb-2">
                                                            <div className="card shadow-sm">
                                                                <div className="card-body p-3">
                                                                    <div className="d-flex justify-content-between align-items-start mb-1">
                                                                        <h6 className="text-primary mb-0 small">{index + 1}. {member.position}</h6>
                                                                        {member.term && (
                                                                            <Badge bg="secondary" className="small">{member.term}</Badge>
                                                                        )}
                                                                    </div>
                                                                    <div className="fw-semibold">
                                                                        {member.name === "TBA" ? (
                                                                            <span className="text-muted fst-italic">To Be Announced</span>
                                                                        ) : (
                                                                            member.name
                                                                        )}
                                                                    </div>
                                                                    {member.affiliation && (
                                                                        <p className="text-muted small mb-0">
                                                                            <i className="bi bi-building me-1"></i>
                                                                            {member.affiliation}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Administration Section */}
                                        {currentBoard.administration && (
                                            <div className="mb-4">
                                                <h5 className="text-warning mb-3">
                                                    <i className="bi bi-briefcase me-2"></i>
                                                    ADMINISTRATION
                                                </h5>
                                                <div className="row g-2">
                                                    {currentBoard.administration.map((member, index) => (
                                                        <div key={index} className="col-12 mb-2">
                                                            <div className="card shadow-sm">
                                                                <div className="card-body p-3">
                                                                    <div className="fw-semibold">{member.position} - {member.name}</div>
                                                                    {member.affiliation && (
                                                                        <p className="text-muted small mb-0">
                                                                            <i className="bi bi-building me-1"></i>
                                                                            {member.affiliation}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-center py-5">
                                        <i className="bi bi-hourglass-split display-4 text-muted mb-3"></i>
                                        <h5 className="text-muted">Board Information for {selectedYear}</h5>
                                        <p className="text-muted">
                                            Board composition details for this year are not yet available.
                                            Please check back later or select a different year.
                                        </p>
                                    </div>
                                )}
                            </Col>

                            {/* Right Column - Past Presidents */}
                            <Col lg={3} className="mb-4">
                                <h5 className="text-secondary mb-3">
                                    <i className="bi bi-trophy me-2"></i>
                                    Past Presidents
                                </h5>
                                <div>
                                    <div className="small">
                                        {pastPresidents.map((president, index) => (
                                            <div key={index} className="mb-2 pb-2 border-bottom">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <Badge bg="primary" className="me-2 small">{president.years}</Badge>
                                                    {president.note && (
                                                        <Badge bg={president.note === "Deceased" ? "dark" : "warning"} className="small">
                                                            {president.note === "Deceased" ? "†" : president.note}
                                                        </Badge>
                                                    )}
                                                </div>
                                                <div className={`fw-semibold mt-1 small ${president.note === "Deceased" ? "text-muted" : ""}`}>
                                                    {president.name}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-3 p-2 bg-light rounded">
                                        <p className="small text-muted mb-0">
                                            <strong>Note:</strong> † indicates deceased members
                                        </p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default BoardOfDirectors;